/**
 * Fleet console contracts — `/agents/workflows` (RO-3-02b).
 *
 * Port of `excellent/app/agents/workflows/_fleet-console.tsx`, which was an
 * async Server Component calling `listFleetLoops(range)` + `portfolioSummary()`
 * against the vault. Both come back in ONE response because the console derives
 * its headline runs/cost/tokens from the ROWS it just rendered — the original's
 * comment says why ("so the strip and the table never disagree"), and two
 * endpoints would reintroduce exactly that possibility.
 *
 * `loopsRunnability` (the KPI strip above the table) already has its own
 * contract in `./runnability.ts` and its own block (`RunnabilityKpi`), which
 * fetches it itself. Nothing here duplicates it.
 *
 * The compiled-workflows section of that page needs no new contract either:
 * `compilations.list` already exists and is already mounted on the Hono host
 * (`excellent/server/routes/models.ts`).
 */

import { z } from "zod";
import type { Operation } from "../../types";
import type { FleetRow } from "../../../blocks/optimization/fleet-table";
import type { PortfolioStats } from "../../../blocks/optimization/portfolio-header";

/**
 * Mirror of `WorkflowRange` in `excellent/src/optimization/db.ts`, which stays
 * the source of truth (`WORKFLOW_RANGES` carries the labels). Duplicated here
 * only as wire validation — `packages/ui` may not import from `excellent/src`.
 */
export const workflowFleetRange = z.enum(["all", "30d", "7d", "24h"]);
export type WorkflowFleetRange = z.infer<typeof workflowFleetRange>;

export interface WorkflowFleetPayload {
  rows: FleetRow[];
  summary: PortfolioStats;
  /** `workflowRangeLabel(range)` — the label the table + strip both print. */
  rangeLabel: string;
}

const listQuery = z.object({ range: workflowFleetRange.optional() });

const listOp: Operation<void, z.infer<typeof listQuery>, void, WorkflowFleetPayload> = {
  method: "GET",
  path: () => "/api/workflows/fleet",
  query: listQuery,
  response: z.custom<WorkflowFleetPayload>(),
};

export const workflowFleet = {
  list: listOp,
};
